import { motion, AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';

const PageTransition = ({ isAnimating, onAnimationComplete, duration }) => {
  // Octopus symbol SVG paths with different opacity classes
  const octopusSymbolPaths = [
    {
      path: "M330.59,283.03c-.47.59-.94,1.17-1.41,1.76-16.77,21.06-13.67,51.7,6.83,69.15,138.77,118.16,256.37,165.84,410.4,170.45,13.94.42,25.46-10.82,25.46-24.77h0c0-13.42-10.69-24.34-24.1-24.75-84.09-2.54-153.12-18.71-221.09-51.49-62.09-29.94-123.93-74.06-196.09-140.35Z",
      opacity: 0.4
    },
    {
      path: "M311.75,414.8c198.99,99.34,278.75,109.19,435.22,109.92,13.73.06,24.9-11.04,24.9-24.77h0c0-13.63-11.03-24.7-24.66-24.77-162.15-.74-234.36-11.33-457.92-127.42-.11.22-.22.44-.32.66-11.98,24.53-1.64,54.18,22.79,66.38Z",
      opacity: 0.6
    },
    {
      path: "M301.5,468.53c191.19,53.5,286.69,56.1,445.58,56.23,13.68.01,24.79-11.08,24.79-24.77h0c0-13.67-11.09-24.76-24.76-24.77-172.05-.13-262.18-3.06-480.38-68.34-.04.16-.09.32-.13.47-7.33,26.44,8.48,53.77,34.9,61.17Z",
      opacity: 0.8
    },
    {
      path: "M747.1,475.23h-467.84c-14.98,0-29.43,5.49-40.63,15.44l-10.5,9.33,10.5,9.33c11.2,9.95,25.65,15.44,40.63,15.44h467.84c13.68,0,24.77-11.09,24.77-24.77h0c0-13.68-11.09-24.77-24.77-24.77Z",
      opacity: 1.0
    },
    {
      path: "M330.59,716.97c-.47-.59-.94-1.17-1.41-1.76-16.77-21.06-13.67-51.7,6.83-69.15,138.77-118.16,256.37-165.84,410.4-170.45,13.94-.42,25.46,10.82,25.46,24.77h0c0,13.42-10.69,24.34-24.1,24.75-84.09,2.54-153.12,18.71-221.09,51.49-62.09,29.94-123.93,74.06-196.09,140.35Z",
      opacity: 0.4
    },
    {
      path: "M311.75,585.2c198.99-99.34,278.75-109.19,435.22-109.92,13.73-.06,24.9,11.04,24.9,24.77h0c0,13.63-11.03,24.7-24.66,24.77-162.15.74-234.36,11.33-457.92,127.42-.11-.22-.22-.44-.32-.66-11.98-24.53-1.64-54.18,22.79-66.38Z",
      opacity: 0.6
    },
    {
      path: "M301.5,531.47c191.19-53.5,286.69-56.1,445.58-56.23,13.68-.01,24.79,11.08,24.79,24.77h0c0,13.67-11.09,24.76-24.76,24.77-172.05.13-262.18,3.06-480.38,68.34-.04-.16-.09-.32-.13-.47-7.33-26.44,8.48-53.77,34.9-61.17Z",
      opacity: 0.8
    }
  ];

  const octopusVariants = {
    initial: { 
      x: '-120%',
      opacity: 0,
      scale: 0.6,
      rotate: -10
    },
    animate: {
      x: '120%',
      opacity: [0, 0.3, 1, 1, 0.3, 0],
      scale: [0.6, 0.9, 1.1, 1.2, 1.3, 1.4],
      rotate: [-10, 0, 5, 0, -5, 10],
      transition: {
        duration: duration * 2,
        ease: [0.25, 0.46, 0.45, 0.94],
        delay: 0.1,
        opacity: {
          times: [0, 0.15, 0.3, 0.7, 0.85, 1],
          duration: duration * 2,
          delay: 0.1
        },
        scale: {
          times: [0, 0.2, 0.4, 0.6, 0.8, 1],
          duration: duration * 2,
          delay: 0.1
        },
        rotate: {
          times: [0, 0.2, 0.4, 0.6, 0.8, 1],
          duration: duration * 2,
          delay: 0.1
        }
      }
    },
    exit: { 
      x: '120%', 
      opacity: 0,
      scale: 1.4,
      rotate: 10
    }
  };

  return (
    <AnimatePresence mode='wait'>
      {isAnimating && (
        <motion.div
          key='octopus-transition'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.3,
            ease: "easeInOut"
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 9999,
            pointerEvents: 'all',
            overflow: 'hidden',
            willChange: 'transform',
            background: 'linear-gradient(135deg, #6511af 0%, #8e44ad 50%, #9b59b6 100%)'
          }}
          onAnimationComplete={() => {
            setTimeout(() => {
              onAnimationComplete('exit');
            }, (duration * 2 + 0.1) * 1000 + 300);
          }}
        >
          <motion.div
            variants={octopusVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{
              position: 'absolute',
              top: '25%',
              left: '30%',
              transform: 'translate(-50%, -50%)',
              width: '500px',
              height: '500px'
            }}
          >
            <svg
              width="100%"
              height="100%"
              viewBox="225 225 550 550"
              style={{
                overflow: 'visible'
              }}
            >
              <g>
                {octopusSymbolPaths.map((pathData, index) => (
                  <motion.path
                    key={index}
                    d={pathData.path}
                    fill={`rgba(255, 255, 255, ${pathData.opacity})`}
                    style={{
                      transformOrigin: 'center'
                    }}
                    initial={{ 
                      opacity: 0,
                      scale: 0.8,
                      rotate: -5
                    }}
                    animate={{ 
                      opacity: [0, pathData.opacity * 0.7, pathData.opacity, pathData.opacity],
                      scale: [0.8, 1.05, 1, 1.02],
                      rotate: [-5, 2, 0, -1],
                      transition: {
                        duration: 0.8,
                        delay: 0.4 + index * 0.08,
                        ease: [0.25, 0.46, 0.45, 0.94],
                        opacity: {
                          times: [0, 0.3, 0.6, 1],
                          duration: 0.8,
                          delay: 0.4 + index * 0.08
                        },
                        scale: {
                          times: [0, 0.4, 0.8, 1],
                          duration: 0.8,
                          delay: 0.4 + index * 0.08
                        },
                        rotate: {
                          times: [0, 0.3, 0.7, 1],
                          duration: 0.8,
                          delay: 0.4 + index * 0.08
                        }
                      }
                    }}
                    exit={{ 
                      opacity: 0,
                      scale: 1.1,
                      rotate: 3
                    }}
                  />
                ))}
              </g>
            </svg>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

PageTransition.propTypes = {
  isAnimating: PropTypes.bool.isRequired,
  onAnimationComplete: PropTypes.func.isRequired,
  duration: PropTypes.number.isRequired
};

export default PageTransition;
